import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { type MetaFunction, Link, useSearchParams } from '@remix-run/react'
import { ArrowLeftIcon } from 'lucide-react'
import { Button } from '#app/components/ui/button'

export const handle: SEOHandle = {
	getSitemapEntries: () => [{ route: '/privacy' }],
}

export const meta: MetaFunction = () => [
	{ title: `Privacy Policy | Erda` },
	{
		name: 'description',
		content: `Privacy policy for Erda.app`,
	},
	{
		name: 'keywords',
		content:
			'privacy, data protection, data security, data collection, data sharing, data usage, data rights, data security, international transfers',
	},
	{ name: 'og:title', content: `Privacy | Erda` },
	{ name: 'og:type', content: 'website' },
	{ name: 'og:url', content: 'https://erda.app' },
	{
		name: 'og:image',
		content: `https://erda.app/image/privacy.png`,
	},
	{ name: 'og:card', content: 'summary_large_image' },
	{ name: 'og:creator', content: '@erda_app' },
	{ name: 'og:site', content: 'https://erda.app' },
	{
		name: 'og:description',
		content: `Privacy policy for Erda.app`,
	},
	{
		name: 'twitter:image',
		content: `https://erda.app/image/privacy.png`,
	},
	{ name: 'twitter:card', content: 'summary_large_image' },
	{ name: 'twitter:creator', content: '@erda_app' },
	{ name: 'twitter:title', content: `Privacy | Erda` },
	{
		name: 'twitter:description',
		content: `Privacy policy for Erda.app`,
	},
]

export default function Privacy() {
	const [searchParams] = useSearchParams()
	const from = searchParams.get('from')
	const fromLabel = from ? from.charAt(1).toUpperCase() + from.slice(2) : null

	return (
		<>
			{from ? (
				<Button
					data-vaul-no-drag
					variant="link"
					className="h-auto w-fit self-center"
					asChild
				>
					<Link to={from}>
						<ArrowLeftIcon className="h-4 w-4" /> {fromLabel}
					</Link>
				</Button>
			) : (
				<Button
					data-vaul-no-drag
					variant="link"
					className="h-auto w-fit self-center"
					asChild
				>
					<Link to="/info">
						<ArrowLeftIcon className="h-4 w-4" /> Info hub
					</Link>
				</Button>
			)}
			<div className="space-y-4 text-sm">
				<h1 className="text-base font-bold">Privacy Policy</h1>
				<p className="!mt-0 text-muted-foreground">
					Last updated: 13th April 2024
				</p>
				<p>
					Welcome to Erda (the "Website"), located at{' '}
					<a href="https://erda.app" className="underline">
						https://erda.app
					</a>{' '}
					Your privacy is of paramount importance to us. This Privacy Policy
					outlines the types of information we collect, how it's used, and the
					measures we take to protect it.
				</p>

				<h2 className="font-bold">1. Data Collection</h2>
				<ul className="!mt-0 pl-4">
					<li className="list-item list-disc">
						Personal Data: We collect personal information that you voluntarily
						provide to us, including but not limited to your name, email
						address, image, website, and bio. This information is collected to
						enhance your experience on our Website and to facilitate our service
						to you.
					</li>
					<li className="list-item list-disc">
						Usage Data: Our servers automatically collect data when you access
						the Website, such as your IP address, browser type, browser version,
						our Website pages that you visit, the time and date of your visit,
						the time spent on those pages, and other diagnostic data.
					</li>
					<li className="list-item list-disc">
						Tracking & Cookies Data: We use cookies and similar tracking
						technologies to track activity on our Website and hold certain
						information. Cookies are files with a small amount of data which may
						include an anonymous unique identifier. Cookies are sent to your
						browser from a website and stored on your device. Tracking
						technologies also used are beacons, tags, and scripts to collect and
						track information and to improve and analyze our Website.
					</li>
				</ul>

				<h2 className="font-bold">2. How We Use Your Data</h2>
				<p className="!mt-0">
					Erda uses the collected data for various purposes:
				</p>
				<ul className="!mt-0 pl-4">
					<li className="list-item list-disc">
						To provide and maintain our Website
					</li>
					<li className="list-item list-disc">
						To notify you about changes to our Website
					</li>
					<li className="list-item list-disc">
						To allow you to participate in interactive features of our Website
						when you choose to do so
					</li>
					<li className="list-item list-disc">To provide customer support</li>
					<li className="list-item list-disc">
						To gather analysis or valuable information so that we can improve
						our Website
					</li>
					<li className="list-item list-disc">
						To monitor the usage of our Website
					</li>
					<li className="list-item list-disc">
						To detect, prevent, and address technical issues
					</li>
				</ul>

				<h2 className="font-bold">3. Communications</h2>
				<p className="!mt-0">
					If you are a registered user and have supplied your email address, we
					may occasionally send you an email to tell you about security, system
					information, new features, solicit your feedback, or keep you up to
					date with what's going on with Erda and our products.There is an
					unsubscribe link located at the bottom of each of the marketing emails
					we send you, so you can opt-out of receiving such emails at any time.
				</p>

				<h2 className="font-bold">4. Data Sharing</h2>
				<p className="!mt-0">
					Erda shares your data with third parties in the following ways:
				</p>
				<ul className="!mt-0 pl-4">
					<li className="list-item list-disc">
						Payment Processing: Information necessary for processing payments is
						shared with Stripe, our payment processing partner. We do not store
						your payment card details.
					</li>
					<li className="list-item list-disc">
						Donation Services: To facilitate donations as part of our service,
						we share transaction details with our chosen charity partners. This
						is limited to the information necessary to process the donation.
					</li>
					<li className="list-item list-disc">
						Analytics and Performance Monitoring: We use tools to monitor the
						performance of our Website and understand how users interact with
						it. This may involve the collection of data regarding your
						interactions on our Website.
					</li>
					<li className="list-item list-disc">
						Your information, including Personal Data, may be transferred to —
						and maintained on — computers located outside of your state,
						province, country, or other governmental jurisdiction where the data
						protection laws may differ from those of your jurisdiction.
					</li>
				</ul>

				<h2 className="font-bold">5. Your Rights</h2>
				<p className="!mt-0">
					Erda aims to take reasonable steps to allow you to correct, amend,
					delete, or limit the use of your Personal Data. You can update your
					account information at any time, and you also have the right to
					request the deletion of your personal data.
				</p>

				<h2 className="font-bold">6. Data Security</h2>
				<p className="!mt-0">
					We take the security of your data seriously and implement all
					necessary measures to protect it against unauthorized access,
					alteration, disclosure, or destruction. However, remember that no
					method of transmission over the internet or method of electronic
					storage is 100% secure.
				</p>

				<h2 className="font-bold">7. International Transfers</h2>
				<p className="!mt-0">
					Your information, including Personal Data, may be transferred to — and
					maintained on — computers located outside of your state, province,
					country, or other governmental jurisdiction where the data protection
					laws may differ than those from your jurisdiction.
				</p>

				<h2 className="font-bold">8. Changes to This Privacy Policy</h2>
				<p className="!mt-0">
					We may update our Privacy Policy from time to time. We will notify you
					of any changes by posting the new Privacy Policy on this page.
				</p>

				<h2 className="font-bold">9. Contact Us</h2>
				<p className="!mt-0">
					If you have any questions about this Privacy Policy, please contact us
					at{' '}
					<Link to="mailto:support@erda.app" className="underline">
						support@erda.app
					</Link>
					.
				</p>
			</div>
		</>
	)
}
